import { Component, EventEmitter, Output } from '@angular/core';
import { SideMenuNode, SideMenuSelectEvent } from '../../../../shared/components/sidemenu/sidemenu.model';
import { AddFilterDraft, AddFilterComponent } from '../add-filter/add-filter.component';
import { SideMenuComponent } from '../../../../shared/components/sidemenu/sidemenu.component';
import { AskPanelComponent } from '../../ask-panel/ask-panel.component';
import { JsonEditorComponent } from '../../../../shared/elements/json-editor/json-editor.component';
import { VisualCodeComponent } from '../../../../shared/elements/visual-code/visual-code.component';

/**
 * Body of the "Add filter" popup: ASK / Visual Editor / JSON Editor / SQL Query tabs via app-sidemenu.
 * Hosted by InbuiltCustomReportComponent inside its own <app-modal>; the modal footer drives this
 * component's public state/methods (primaryActionLabel/canPerformPrimaryAction/onPrimaryAction), the
 * same pattern used by select-datasource-modal's #dsBody.
 */
@Component({
    selector: 'app-add-filter-modal',
    templateUrl: './add-filter-modal.component.html',
    styleUrl: './add-filter-modal.component.scss',
    imports: [SideMenuComponent, AskPanelComponent, AddFilterComponent, JsonEditorComponent, VisualCodeComponent]
})
export class AddFilterModalComponent {
  @Output() cancelled = new EventEmitter<void>();
  @Output() filterCreated = new EventEmitter<any>();

  readonly menuItems: SideMenuNode[] = [
    { id: 'ask', type: 'ask', name: 'ASK' },
    { id: 'visual-editor', type: 'editor', name: 'Visual Editor' },
    { id: 'json-editor', type: 'editor', name: 'JSON Editor' },
    { id: 'sql-query', type: 'query', name: 'SQL Query' }
  ];
  selectedMenuId = 'ask';

  askPromptValue = '';
  // Visual Editor / JSON Editor / SQL Query all start empty until the user fills them in (or ASK generates a draft).
  filterDraft: AddFilterDraft | null = null;
  jsonEditorInitialJson: any = {};
  sqlQueryCode = '';

  onMenuSelected(event: SideMenuSelectEvent): void {
    if (event?.node?.id) {
      this.selectedMenuId = event.node.id;
    }
  }

  // app-modal keeps this component mounted between opens (see select-datasource-modal-pattern memory) -
  // the host must call this before showing the modal again so a previous draft isn't still here.
  resetState(): void {
    this.selectedMenuId = 'ask';
    this.askPromptValue = '';
    this.filterDraft = null;
    this.jsonEditorInitialJson = {};
    this.sqlQueryCode = '';
  }

  onFilterDraftChange(draft: AddFilterDraft): void {
    this.filterDraft = draft;
  }

  onJsonTextChange(value: any): void {
    this.jsonEditorInitialJson = value;
  }

  get primaryActionLabel(): string {
    return this.selectedMenuId === 'ask' ? 'Generate' : 'Add filter';
  }

  get canPerformPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') {
      return !!this.askPromptValue.trim();
    }
    return !!this.filterDraft && !!this.filterDraft.label.trim() && !!this.filterDraft.name.trim() && !!this.filterDraft.column.trim();
  }

  onPrimaryAction(): void {
    if (this.selectedMenuId === 'ask') {
      // TODO: wire to the real ASK-generation endpoint once available; for now just move to Visual Editor for review.
      this.selectedMenuId = 'visual-editor';
      return;
    }
    this.filterCreated.emit({
      draft: this.filterDraft,
      jsonEditor: this.jsonEditorInitialJson,
      sqlQuery: this.sqlQueryCode
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
